import React, { useEffect, useState } from 'react';
import {
    DollarSign, Plus, Trash2, Loader, X, CheckCircle, AlertCircle, Pencil,
    ToggleLeft, ToggleRight, Building2,
} from 'lucide-react';
import { adminService, BillingPlan } from '../services/AdminService';

// Platform-admin builder for billing plans: the commercial quote-agreement
// terms (price / included quotes / overage / per-quote allowances) plus
// optional monthly hard caps per metric. Plans are assigned to organizations
// in the Orgs tab; org-level agreement fields override a plan's values.

const METRIC_LABELS: Record<string, string> = {
    orders: 'Orders',
    part_queries: 'Part searches',
    llm_queries: 'LLM queries',
    quotes: 'Quotes',
    images: 'Images',
    pdfs: 'PDFs',
};
const METRICS = Object.keys(METRIC_LABELS);

interface FormState {
    name: string;
    description: string;
    monthly_price: string;   // dollars in the UI, cents over the wire
    included_quotes: string;
    overage_price: string;   // dollars
    allowance_searches: string;
    allowance_images: string;
    allowance_pdfs: string;
    caps: Record<string, string>; // metric -> monthly cap ('' = unlimited)
    is_active: boolean;
}

const EMPTY_FORM: FormState = {
    name: '',
    description: '',
    monthly_price: '',
    included_quotes: '',
    overage_price: '',
    allowance_searches: '',
    allowance_images: '',
    allowance_pdfs: '',
    caps: {},
    is_active: true,
};

const toCents = (dollars: string): number | null =>
    dollars.trim() === '' ? null : Math.round(parseFloat(dollars) * 100);
const toInt = (v: string): number | null => (v.trim() === '' ? null : parseInt(v));
const fromCents = (cents: number | null | undefined): string =>
    cents == null ? '' : String(cents / 100);
const money = (cents: number | null | undefined, currency = 'USD'): string =>
    cents == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

const BillingPlansTab: React.FC = () => {
    const [plans, setPlans] = useState<BillingPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionMsg, setActionMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);

    const notify = (text: string, type: 'success' | 'error') => {
        setActionMsg({ text, type });
        setTimeout(() => setActionMsg(null), 5000);
    };

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await adminService.listBillingPlans();
            setPlans(res.data?.plans || []);
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to load billing plans');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setShowForm(true);
    };

    const openEdit = (plan: BillingPlan) => {
        setEditingId(plan.id);
        setForm({
            name: plan.name,
            description: plan.description || '',
            monthly_price: fromCents(plan.monthly_price_cents),
            included_quotes: plan.included_quotes_monthly == null ? '' : String(plan.included_quotes_monthly),
            overage_price: fromCents(plan.overage_price_cents),
            allowance_searches: plan.allowance_searches == null ? '' : String(plan.allowance_searches),
            allowance_images: plan.allowance_images == null ? '' : String(plan.allowance_images),
            allowance_pdfs: plan.allowance_pdfs == null ? '' : String(plan.allowance_pdfs),
            caps: Object.fromEntries(
                Object.entries(plan.metric_limits || {}).map(([k, v]) => [k, v == null ? '' : String(v)]),
            ),
            is_active: plan.is_active,
        });
        setShowForm(true);
    };

    const buildBody = () => {
        const caps: Record<string, number> = {};
        for (const m of METRICS) {
            const v = (form.caps[m] || '').trim();
            if (v !== '') caps[m] = parseInt(v);
        }
        return {
            name: form.name.trim(),
            description: form.description.trim() || null,
            monthly_price_cents: toCents(form.monthly_price),
            included_quotes_monthly: toInt(form.included_quotes),
            overage_price_cents: toCents(form.overage_price),
            allowance_searches: toInt(form.allowance_searches),
            allowance_images: toInt(form.allowance_images),
            allowance_pdfs: toInt(form.allowance_pdfs),
            metric_limits: Object.keys(caps).length ? caps : null,
            is_active: form.is_active,
        };
    };

    const handleSave = async () => {
        if (!form.name.trim()) { notify('Plan name is required', 'error'); return; }
        setSaving(true);
        try {
            const body = buildBody();
            if (editingId != null) {
                // PUT treats omitted/null as "unchanged"; empty inputs must clear.
                const clear_fields = Object.entries(body)
                    .filter(([k, v]) => v == null && k !== 'name' && k !== 'is_active' && k !== 'currency')
                    .map(([k]) => k);
                await adminService.updateBillingPlan(editingId, { ...body, clear_fields });
                notify(`Plan "${body.name}" updated`, 'success');
            } else {
                await adminService.createBillingPlan(body);
                notify(`Plan "${body.name}" created`, 'success');
            }
            setShowForm(false);
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to save plan', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (plan: BillingPlan) => {
        setBusyId(plan.id);
        try {
            await adminService.updateBillingPlan(plan.id, { is_active: !plan.is_active });
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to update plan', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (plan: BillingPlan) => {
        if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
        setBusyId(plan.id);
        try {
            await adminService.deleteBillingPlan(plan.id);
            notify(`Plan "${plan.name}" deleted`, 'success');
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to delete plan', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const capsSummary = (plan: BillingPlan): string => {
        const entries = Object.entries(plan.metric_limits || {}).filter(([, v]) => v != null);
        if (!entries.length) return '—';
        return entries.map(([k, v]) => `${METRIC_LABELS[k] || k}: ${Number(v).toLocaleString()}`).join(' · ');
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-[#1E0DB8]" /> Billing Plans
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Reusable plan definitions: quote-agreement terms plus optional monthly caps per metric.
                        Assign a plan to an organization in the Orgs tab; org-level agreement fields override the plan.
                    </p>
                </div>
                <button
                    onClick={openCreate}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-[#1E0DB8] hover:bg-[#2a1ca8]"
                >
                    <Plus className="w-4 h-4" /> New plan
                </button>
            </div>

            {actionMsg && (
                <div className={`mb-3 p-2.5 rounded-lg text-sm flex items-center gap-2 ${
                    actionMsg.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                    {actionMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {actionMsg.text}
                </div>
            )}
            {error && <div className="mb-3 p-2.5 rounded-lg bg-red-50 text-sm text-red-800">{error}</div>}

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
                    <Loader className="w-4 h-4 animate-spin" /> Loading plans…
                </div>
            ) : plans.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-xl p-8 text-center text-sm text-gray-500">
                    No billing plans yet. Create the first one — e.g. “Small: $1,000/month, 1,250 quotes included,
                    $0.80 per extra quote, each quote bundles 3 searches / 3 images / 1 PDF”.
                </div>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200">
                                <th className="py-2.5 px-3">Plan</th>
                                <th className="py-2.5 px-3">Monthly price</th>
                                <th className="py-2.5 px-3">Included quotes</th>
                                <th className="py-2.5 px-3">Overage / quote</th>
                                <th className="py-2.5 px-3">Bundle (S/I/P)</th>
                                <th className="py-2.5 px-3">Caps</th>
                                <th className="py-2.5 px-3">Orgs</th>
                                <th className="py-2.5 px-3">Status</th>
                                <th className="py-2.5 px-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map((plan) => (
                                <tr key={plan.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                                    <td className="py-2.5 px-3">
                                        <div className="font-medium text-gray-900">{plan.name}</div>
                                        {plan.description && <div className="text-xs text-gray-500">{plan.description}</div>}
                                    </td>
                                    <td className="py-2.5 px-3">{money(plan.monthly_price_cents, plan.currency)}</td>
                                    <td className="py-2.5 px-3">{plan.included_quotes_monthly == null ? '—' : plan.included_quotes_monthly.toLocaleString()}</td>
                                    <td className="py-2.5 px-3">{money(plan.overage_price_cents, plan.currency)}</td>
                                    <td className="py-2.5 px-3 text-gray-600">
                                        {plan.allowance_searches ?? 3}/{plan.allowance_images ?? 3}/{plan.allowance_pdfs ?? 1}
                                    </td>
                                    <td className="py-2.5 px-3 text-xs text-gray-600 max-w-[220px]">{capsSummary(plan)}</td>
                                    <td className="py-2.5 px-3">
                                        <span className="inline-flex items-center gap-1 text-gray-600">
                                            <Building2 className="w-3.5 h-3.5" /> {plan.orgs_assigned}
                                        </span>
                                    </td>
                                    <td className="py-2.5 px-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            plan.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                            {plan.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="py-2.5 px-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <button
                                                onClick={() => openEdit(plan)}
                                                title="Edit plan"
                                                className="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleToggleActive(plan)}
                                                disabled={busyId === plan.id}
                                                title={plan.is_active ? 'Deactivate (hidden from assignment; assigned orgs keep it)' : 'Activate'}
                                                className="p-1.5 rounded hover:bg-gray-200 text-gray-600 disabled:opacity-50"
                                            >
                                                {plan.is_active ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(plan)}
                                                disabled={busyId === plan.id || plan.orgs_assigned > 0}
                                                title={plan.orgs_assigned > 0 ? `Assigned to ${plan.orgs_assigned} org(s) — unassign first` : 'Delete plan'}
                                                className="p-1.5 rounded hover:bg-red-100 text-red-600 disabled:opacity-40"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create / edit modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowForm(false)}>
                    <div
                        className="bg-white rounded-xl p-5 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-base font-semibold text-gray-900">
                                {editingId != null ? 'Edit plan' : 'New plan'}
                            </h4>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder='e.g. "Small"'
                                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                <input
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder="Optional note shown to admins"
                                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Monthly price (USD)</label>
                                <input
                                    type="number" min="0" step="0.01" placeholder="e.g. 1000"
                                    value={form.monthly_price}
                                    onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
                                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Included quotes / month</label>
                                <input
                                    type="number" min="0" step="1" placeholder="e.g. 1250"
                                    value={form.included_quotes}
                                    onChange={(e) => setForm({ ...form, included_quotes: e.target.value })}
                                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Overage price / quote (USD)</label>
                                <input
                                    type="number" min="0" step="0.01" placeholder="e.g. 0.80"
                                    value={form.overage_price}
                                    onChange={(e) => setForm({ ...form, overage_price: e.target.value })}
                                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {([
                                    ['allowance_searches', 'Searches / quote', '3'],
                                    ['allowance_images', 'Images / quote', '3'],
                                    ['allowance_pdfs', 'PDFs / quote', '1'],
                                ] as const).map(([field, label, ph]) => (
                                    <div key={field}>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                                        <input
                                            type="number" min="1" step="1" placeholder={ph}
                                            value={form[field]}
                                            onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                                            className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Monthly caps per metric <span className="font-normal text-gray-500">(empty = unlimited)</span>
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {METRICS.map((m) => (
                                    <div key={m} className="flex items-center gap-2">
                                        <span className="text-xs text-gray-600 w-24 flex-shrink-0">{METRIC_LABELS[m]}</span>
                                        <input
                                            type="number" min="1" step="1" placeholder="∞"
                                            value={form.caps[m] || ''}
                                            onChange={(e) => setForm({ ...form, caps: { ...form.caps, [m]: e.target.value } })}
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                        />
                                    </div>
                                ))}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                Caps are enforced per the org's enforcement stage (off / warn / enforce) in the Orgs tab.
                                Overage on <em>quotes</em> is billed, not blocked, unless a quotes cap is set here and the org is enforced.
                            </p>
                        </div>

                        <label className="flex items-center gap-2 mt-4 cursor-pointer select-none w-fit">
                            <input
                                type="checkbox"
                                checked={form.is_active}
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                            />
                            <span className="text-sm text-gray-700">Active (selectable for assignment)</span>
                        </label>

                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={() => setShowForm(false)}
                                className="px-3.5 py-2 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-[#1E0DB8] hover:bg-[#2a1ca8] disabled:opacity-60"
                            >
                                {saving && <Loader className="w-3.5 h-3.5 animate-spin" />}
                                {editingId != null ? 'Save changes' : 'Create plan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingPlansTab;
